import { create } from "zustand";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

export interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

interface ConfirmState {
  request: (ConfirmOptions & { resolve: (ok: boolean) => void }) | null;
  open: (opts: ConfirmOptions) => Promise<boolean>;
  settle: (ok: boolean) => void;
}

const useConfirmStore = create<ConfirmState>((set, get) => ({
  request: null,
  open: (opts) =>
    new Promise<boolean>((resolve) => {
      set({ request: { ...opts, resolve } });
    }),
  settle: (ok) => {
    get().request?.resolve(ok);
    set({ request: null });
  },
}));

/** `const ok = await confirm({ title: 'Delete pipeline?', destructive: true })` */
export function useConfirm() {
  return useConfirmStore((s) => s.open);
}

/** Renders the confirm dialog. Mount once, near the app root. */
export function ConfirmDialogHost() {
  const request = useConfirmStore((s) => s.request);
  const settle = useConfirmStore((s) => s.settle);

  return (
    <Dialog open={request !== null} onClose={() => settle(false)} maxWidth="xs" fullWidth>
      <DialogTitle>{request?.title}</DialogTitle>
      {request?.description ? (
        <DialogContent>
          <DialogContentText>{request.description}</DialogContentText>
        </DialogContent>
      ) : null}
      <DialogActions>
        <Button onClick={() => settle(false)} color="inherit">
          {request?.cancelLabel ?? "Cancel"}
        </Button>
        <Button
          onClick={() => settle(true)}
          variant="contained"
          color={request?.destructive ? "error" : "primary"}
        >
          {request?.confirmLabel ?? "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
